// TODO(kim): use shared SASS/JS variables

import { useEffect, useRef, useState } from 'react';

import useThrottle from './hooks/useThrottle';
import { type CancellablePromise, makeCancellablePromise } from './promise';

// https://itnext.io/sharing-variables-between-js-and-sass-using-webpack-sass-loader-713f51fa7fa0
export const MOBILE_WIDTH = 824; // also modify styles/_defs

/**
 * Returns whether the given dom element is currently visible.
 */
export type BoundingClientRect = {
  bottom: number;
  right: number;
  left: number;
  top: number;
};
export function isDomElementVisible(domElement: HTMLElement): boolean {
  if (!domElement) {
    return false;
  }
  const rect = domElement.getBoundingClientRect();

  const { left, right, top, bottom }: BoundingClientRect = rect;
  return (
    bottom > 0 &&
    right > 0 &&
    left < (window.innerWidth || document.documentElement.clientWidth) &&
    top < (window.innerHeight || document.documentElement.clientHeight)
  );
}

// Make sure to update dom_test_utils.ts when screen widths overlap here.
// Make sure to also update styles/_defs.scss when screen widths are changed and/or added.
export enum ScreenWidth {
  MOBILE = '(max-width: 1023px)',
  DESKTOP = '(min-width: 1024px)',
}
/**
 * Hook that returns whether the screen width matches the given screen width.
 */
export function useMatchScreenWidth(screenWidth: ScreenWidth): boolean {
  if (!window.matchMedia) {
    throw new Error('window.matchMedia is not defined');
  }
  const [matches, setMatches] = useState(window.matchMedia(screenWidth).matches);

  useEffect(() => {
    const mediaQueryList = window.matchMedia(screenWidth);
    setMatches(mediaQueryList.matches);
    function handleMediaQueryChange(event: MediaQueryListEvent): void {
      setMatches(event.matches);
    }
    mediaQueryList.addEventListener('change', handleMediaQueryChange);

    return (): void => mediaQueryList.removeEventListener('change', handleMediaQueryChange);
  }, [screenWidth]);

  return matches;
}

// Register a callback to be called when the tab visibilty changes, returning a function to
// remove the event listener when it is no longer needed. This can be used to stop requesting
// data when the user isn't viewing the page.
// Approach taken from https://developer.mozilla.org/en-US/docs/Web/API/Page_Visibility_API
export function onDocumentVisibiltyChange(
  handleVisibilityChange: (isHidden: boolean) => void,
): () => void {
  let hidden: string;
  let visibilityChange: string;
  if (typeof document.hidden !== 'undefined') {
    // Opera 12.10 and Firefox 18 and later support
    hidden = 'hidden';
    visibilityChange = 'visibilitychange';
  } else if (typeof (document as any).msHidden !== 'undefined') {
    hidden = 'msHidden';
    visibilityChange = 'msvisibilitychange';
  } else if (typeof (document as any).webkitHidden !== 'undefined') {
    hidden = 'webkitHidden';
    visibilityChange = 'webkitvisibilitychange';
  } else {
    console.warn('Visibility change detection is unsupported in this browser');
    return (): void => {};
  }
  let previousIsHidden: boolean | null = null;
  const visibilityChangeCallback = (): void => {
    // Hidden if the document is hidden and a fullscreen element is not present.
    const isHidden = (document as any)[hidden] && document.fullscreenElement === null;
    if (previousIsHidden === null || isHidden !== previousIsHidden) {
      handleVisibilityChange(isHidden);
    }
    previousIsHidden = isHidden;
  };
  document.addEventListener(visibilityChange, visibilityChangeCallback, false);
  return (): void => {
    document.removeEventListener(visibilityChange, visibilityChangeCallback);
  };
}

/**
 * Waits for the current animation on the given html element to end.
 * Can be called immediately before or after the animation has started, e.g. changing the class name.
 */
export function waitForAnimationEnd(targetElement: HTMLElement): Promise<void> {
  const transitionPromise = new Promise<void>((resolve) => {
    function handleAnimationEnd(): void {
      resolve();
      targetElement.removeEventListener('animationend', handleAnimationEnd);
    }
    targetElement.addEventListener('animationend', handleAnimationEnd);
  });
  return transitionPromise;
}

export const DEFAULT_SCROLL_DURATION_MS = 300;

/**
 * Returns the current progress using an ease-in-out easing equation.
 * @param timeProgress A number between 0 and 1 representing the current time progress within the duration of the animation.
 */
function easeInOut(timeProgress: number): number {
  return timeProgress < 0.5
    ? 2 * timeProgress * timeProgress
    : -1 + (4 - 2 * timeProgress) * timeProgress;
}

export function animateValue(
  startValue: number,
  endValue: number,
  animationDuration: number,
  updateCallback: (currentValue: number) => void,
): CancellablePromise<boolean> {
  let isCancelled = false;
  // Store the initial value and total difference for calculating animation progress later on.
  const valueDifference = endValue - startValue;
  const animatePromise = new Promise<boolean>((resolve) => {
    const startTime = performance.now();
    const animateLoop = (): void => {
      // If the animation has been canceled, exit the loop unsuccessfully.
      if (isCancelled) {
        resolve(false);
        return;
      }
      const currentTime = performance.now();
      // Calculate the time progress between 0 and 1.
      const timeElapsed = currentTime - startTime;
      const timeProgress = Math.min(timeElapsed / animationDuration, 1);
      // Calculate how much animation progress should be made using the easing equation.
      const easedProgress = easeInOut(timeProgress);
      const newValue = startValue + valueDifference * easedProgress;
      updateCallback(newValue);
      if (newValue === endValue) {
        resolve(true);
        return;
      }
      // Smooth scrolling by using requestAnimationFrame.
      requestAnimationFrame(animateLoop);
    };
    animateLoop();
  });
  return makeCancellablePromise(animatePromise, (): void => {
    isCancelled = true;
  });
}

/**
 * Use to animate scrolling of a scroll container to a given scrollTop destination.
 * Use this instead of `scrollTo` when you want to await the completion of the scroll animation.
 * @param scrollContainer The scroll container to scroll.
 * @param scrollTopDestination The scrollTop destination to scroll to.
 * @param scrollDurationMs The duration of the scroll animation in milliseconds.
 * @param offset The offset from the top of the scrollDestination to scroll to.
 */
export function animateScrollTo(
  scrollContainer: HTMLElement,
  scrollTopDestination: number,
  scrollDurationMs = DEFAULT_SCROLL_DURATION_MS,
  offset = 0,
): CancellablePromise<boolean> {
  scrollTopDestination += offset;
  const scrollContainerRect = scrollContainer.getBoundingClientRect();
  // When the scroll destination is unreachable (i.e. the scrollTop is the scrollHeight),
  // scroll to the bottom of the scrollContainer.
  // We use Math.ceil because scrollHeight is an integer, but rect.height is a float.
  if (scrollTopDestination > scrollContainer.scrollHeight - Math.ceil(scrollContainerRect.height)) {
    scrollTopDestination = scrollContainer.scrollHeight - Math.ceil(scrollContainerRect.height);
  }
  // When the scroll destination is negative, scroll to the top of the scrollContainer.
  if (scrollTopDestination < 0) {
    scrollTopDestination = 0;
  }
  return animateValue(
    scrollContainer.scrollTop,
    scrollTopDestination,
    scrollDurationMs,
    (value) => {
      scrollContainer.scrollTop = value;
    },
  );
}

/**
 * Use to animate scrolling of a scroll container to a given element.
 * Use this instead of `scrollIntoview` when you want to await the completion of the scroll animation.
 * @param scrollContainer The scroll container to scroll.
 * @param scrollDestination The element to scroll to.
 * @param scrollDurationMs The duration of the scroll animation in milliseconds.
 * @param offset The offset from the top of the scrollDestination to scroll to.
 */
export function animateScrollToElement(
  scrollContainer: HTMLElement,
  scrollDestination: HTMLElement,
  scrollDurationMs = DEFAULT_SCROLL_DURATION_MS,
  offset = 0,
): CancellablePromise<boolean> {
  const scrollDestinationTop = scrollDestination.getBoundingClientRect().top;
  const scrollDestinationBottom = scrollDestination.getBoundingClientRect().bottom;
  const scrollContainerTop = scrollContainer.getBoundingClientRect().top;
  const scrollContainerBottom = scrollContainer.getBoundingClientRect().bottom;
  if (
    // Top of scroll container is within scroll destination.
    (scrollDestinationTop <= scrollContainerTop && scrollContainerTop <= scrollDestinationBottom) ||
    // Scroll destination is within scroll container.
    (scrollContainerTop <= scrollDestinationTop && scrollDestinationBottom <= scrollContainerBottom)
  ) {
    // TODO(platform): Flash only if the node is a new node in the view or not previously selected.
    return makeCancellablePromise(Promise.resolve(true), (): void => {});
  }
  const scrollTopDestination =
    scrollDestinationTop - scrollContainerTop + scrollContainer.scrollTop;
  return animateScrollTo(scrollContainer, scrollTopDestination, scrollDurationMs, offset);
}

// "Inactivity" is typically defined on the order of minutes/hours, so we'll use
// some reasonable (but arbitrary) intervals here to check frequently, but not too often.
export const ACTIVITY_CHECK_INTERVAL_MS = 10 * 1000; // 10 seconds
const ACTIVITY_HANDLE_THROTTLE_INTERVAL_MS = 1000; // 1 second
export const ACTIVITY_EVENTS: Array<keyof GlobalEventHandlersEventMap> = [
  'mousemove',
  'mousedown',
  'keydown',
  'scroll',
];
export const LAST_ACTIVITY_TIME_KEY = 'lastActivityTime';

export interface UseInactivityCallbackArgs {
  /** Function to be triggered after inactivityTimeThresholdMs elapses. */
  callback: () => void;
  /** Time to wait before triggering callback. */
  inactivityTimeThresholdMs: number;
  /** Whether the callback should be triggered. */
  enabled: boolean;
}

/**
 * Hook that fires a callback when the user has been inactive (from mouse and keyboard down) for
 * the input time.
 *
 * Gotchas:
 * - We don't store any state locally here, instead keeping a watermark in localStorage.
 *   - This watermark is reused across different tabs, so activity in one tab should keep
 *     all tabs active.
 *   - The watermark outlives the tab, so a user who closed the tab and came back after the
 *     threshold is treated as inactive on mount.
 * - `callback` will only be fired a single time the first time inactivity is detected.
 */
export function useInactivityCallback({
  callback,
  inactivityTimeThresholdMs,
  enabled,
}: UseInactivityCallbackArgs): void {
  const hasFiredCallbackRef = useRef(false);
  const handleActivity = useThrottle(() => {
    window.localStorage.setItem(LAST_ACTIVITY_TIME_KEY, Date.now().toString());
  }, ACTIVITY_HANDLE_THROTTLE_INTERVAL_MS);

  const hasCheckedStoredWatermarkRef = useRef(false);

  useEffect(
    function checkStoredWatermarkOnMount() {
      if (hasCheckedStoredWatermarkRef.current) {
        return;
      }
      hasCheckedStoredWatermarkRef.current = true;

      const storedActivityTime = window.localStorage.getItem(LAST_ACTIVITY_TIME_KEY);
      const isStale =
        storedActivityTime !== null &&
        Date.now() - Number(storedActivityTime) >= inactivityTimeThresholdMs;
      if (enabled && isStale) {
        hasFiredCallbackRef.current = true;
        callback();
        return;
      }
      // Refresh even when tracking is disabled so a later enable never sees a stale watermark.
      window.localStorage.setItem(LAST_ACTIVITY_TIME_KEY, Date.now().toString());
    },
    [callback, inactivityTimeThresholdMs, enabled],
  );

  useEffect(
    function setupActivityHandlers() {
      if (!enabled) {
        return;
      }

      function cleanup(): void {
        ACTIVITY_EVENTS.forEach((event) => {
          document.removeEventListener(event, handleActivity);
        });
        clearInterval(intervalId);
      }

      ACTIVITY_EVENTS.forEach((event) => {
        document.addEventListener(event, handleActivity);
      });

      // Periodically check if the user has been inactive.
      const intervalId = setInterval(function checkInactivity() {
        const lastActivityTime = Number(window.localStorage.getItem(LAST_ACTIVITY_TIME_KEY));
        if (
          !hasFiredCallbackRef.current &&
          Date.now() - lastActivityTime >= inactivityTimeThresholdMs
        ) {
          hasFiredCallbackRef.current = true;
          callback();
          cleanup();
        }
      }, ACTIVITY_CHECK_INTERVAL_MS);

      return cleanup;
    },
    [callback, inactivityTimeThresholdMs, handleActivity, enabled],
  );
}
